import * as Sentry from '@sentry/vue';
import { capitalizeFirstLetter } from '@/helpers/StringHelpers';
import UserService from '@/services/users/UserService';
import CouponsHistoryService from '@/services/cms/couponsHistoryService';
import boxType from '@/assets/images/decoration/box-type.svg';
import delivery from '@/assets/images/decoration/delivery.svg';
import holiday from '@/assets/images/decoration/holiday.svg';

const getDefaultState = () => {
    return {
        user: {
            hasFetched: false,
            data: null,
            banners: null,
        },
        rescheduledDeliveryDays: {
            data: [],
        },
        expandFormName: null,
        countrySwitch: {
            hasAnswered: localStorage.getItem('country-switch-answered'),
            location: null,
        },
        CouponsHistory: null,
        signupCoupon: null,
        skipReasons: null,
        holidayGiftingEnabled: null,
        shiftDeliveryScheduleEnabled: false,
        skipConsecutiveHolidayEnabled: false,
        scrollbarEnabled: localStorage.getItem('scrollbar-enabled'),
        outstandingOrders: [],
        setupIntent: null,
        fetchingSetupIntent: false,
        hasError: false,
        showFloatingBanner: false,
        bannerMessage: null,
        canPauseOrCancel: true,
        isPauseFlow: false,
        restartFields: {
            'heading': 'How does it work?',
            'paragraph_without_delivery': '',
            'image_type': 'photo',
            'carousel_on_mobile': false,
            'rounded_image': true,
            'show_discount_copy': true,
            'section': [
                {
                    'image': 'https://cdn.buttercms.com/DiOWOULxQnej3pn6h0dY',
                    'heading': 'We pick the flowers',
                    'paragraph': 'Freddie curates the perfect arrangements, and work direct with growers to get you the best flowers.',
                },
                {
                    'image': 'https://cdn.buttercms.com/ZaQgoRdOQyWKsA462SbN',
                    'heading': 'and deliver them to you',
                    'paragraph': "We'll bring your box direct to your door. All our deliveries are certified Carbon Neutral.",
                },
                {
                    'image': 'https://cdn.buttercms.com/zNpr8ZKpSK6hZcJ2DUF4',
                    'heading': 'Learn to arrange like a florist',
                    'paragraph': "We teach you all about flowers with Freddie's arranging guides - then just sit back and admire your creation.",
                },
                {
                    'image': 'https://cdn.buttercms.com/hXnOEdRSGyn8HHNAOxCg',
                    'heading': "You're in control - no commitment",
                    'paragraph': 'Skip any delivery using the online calendar, and have flowers when it suits you. Cancel any time.',
                },
            ],
        },
        updateYourPlanData: {
            image_type: 'graphic',
            section: [
                {
                    heading: 'Change your box type',
                    paragraph: 'We have 3 box types to suite any budget',
                    image: boxType,
                },
                {
                    heading: 'Update your delivery frequency',
                    paragraph: 'Make savings by reducing your deliveries',
                    image: delivery,
                },
                {
                    heading: 'Take a holiday',
                    paragraph: 'Sometimes it\'s good to take a break',
                    image: holiday,
                },
            ],
        },
        pauseFeedbackOptions: [
            {
                id: 1,
                label: "I'm going away/moving house/renovating my place",
            },
            {
                id: 2,
                label: 'I prefer to order occassionally',
            },
            {
                id: 3,
                label: "It's out of my budget right now",
            },
        ],
        promotionalBanners: [],
        isMobile: false,
        isDesktop: false,
        themes: null,
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },
        setUserGeolocation: (state, payload) => state.countrySwitch.location = payload,

        countrySwitchHasAnswered: (state, bool) => {
            state.countrySwitch.hasAnswered = bool;
            localStorage.setItem('country-switch-answered', bool);
        },

        setScrollbarEnabled: (state, bool) => {
            state.scrollbarEnabled = bool;

            if (bool) {
                document.getElementById('scrollbar').innerHTML = '::-webkit-scrollbar{  }';
            } else {
                document.getElementById('scrollbar').innerHTML = '::-webkit-scrollbar{ display: none }';
            }

            localStorage.setItem('scrollbar-enabled', bool);
        },

        updateUserData(state, payload) {
            state.user.data = payload;
        },

        updateSkipReasons(state, payload) {
            state.skipReasons = payload;
        },

        updateUserBanners(state, payload) {
            state.user.banners = payload;
        },

        updateCouponsHistory(state, payload) {
            state.CouponsHistory = payload;
        },

        updateExpandFormName(state, payload) {
            state.expandFormName = payload;
        },

        updateRescheduledDeliveryDays(state, payload) {
            state.rescheduledDeliveryDays.data = payload;
        },

        setSignupCoupon(state, payload) {
            state.signupCoupon = payload;
        },

        updateOutstandingOrders(state, payload) {
            state.outstandingOrders = payload;
        },

        setHolidayGiftingEnabled: (state, payload) => state.holidayGiftingEnabled = payload,
        setShiftDeliveryScheduleEnabled: (state, payload) => state.shiftDeliveryScheduleEnabled = payload,
        setSkipConsecutiveHolidayEnabled: (state, payload) => state.skipConsecutiveHolidayEnabled = payload,
        resetCouponState(state) {
            state.signupCoupon = null;
        },
        setSetupIntent: (state, payload) => state.setupIntent = payload,

        setFetchingSetupIntent: (state, payload) => state.fetchingSetupIntent = payload,

        setError: (state, payload) => state.hasError = payload,

        setShowFloatingBanner: (state, payload) => state.showFloatingBanner = payload,

        setBannerMessage: (state, payload) => state.bannerMessage = payload,

        setCanPauseOrCancel: (state, payload) => state.canPauseOrCancel = payload,

        setIsPauseFlow: (state, payload) => state.isPauseFlow = payload,

        setPromotionalBanners: (state, payload) => state.promotionalBanners = payload,

        setIsMobile: (state, payload) => state.isMobile = payload,

        setIsDesktop: (state, payload) => state.isDesktop = payload,

        setTheme: (state, payload) => state.themes = payload,
    },
    actions: {
        fetchUserData({ commit, state, rootGetters, dispatch }, forceReload = false) {
            commit('setError', false);
            if (state.user.hasFetched === false || forceReload === true) {
                state.user.hasFetched = true;
                let service = new UserService();

                return new Promise((resolve, reject) => {
                    try {
                        service.fetchUserData(rootGetters['auth/isCanvasser'])
                            .then(response => {
                                commit('updateUserData', response.data.data);
                                commit('updateUserBanners', response.data.banners);
                                commit('setHolidayGiftingEnabled', response.data.data.holiday_gifting);
                                commit('setShiftDeliveryScheduleEnabled', response.data.data.early_defer);
                                commit('setSkipConsecutiveHolidayEnabled', response.data.data.change_plan);
                                dispatch('cookies/mountStripe', {}, { root: true });
                                resolve(response);
                            })
                            .catch(error => {
                                state.user.hasFetched = false;
                                console.error({ error });
                                reject(error);
                            });
                    } catch (err) {
                        Sentry.captureException(err);
                    }
                });
            }
        },

        fetchOutstandingOrders({ commit, state, rootGetters }) {
            let service = new UserService();

            return new Promise((resolve, reject) => {
                service.getOutstandingOrders(rootGetters['account/getUserId'])
                    .then(response => {
                        commit('updateOutstandingOrders', response.data);
                    })
                    .catch(error => reject(error));
            });
        },

        payOutstandingOrders({ commit, state, rootGetters }, payload) {
            let service = new UserService();

            return new Promise((resolve, reject) => {
                service.payOutstandingOrders(rootGetters['account/getUserId'], payload)
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => reject(error));
            });
        },

        fetchSkipDeliveryReasons({ commit }) {
            let service = new UserService();

            return new Promise((resolve, reject) => {
                service.fetchSkipDeliveryReasons()
                    .then(response => {
                        commit('updateSkipReasons', response.data.data);
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },
        sendSkipDeliveryReasons({ commit }, data) {
            let service = new UserService();
            return new Promise((resolve, reject) => {
                service.sendSkipDeliveryReasons(data.subscriptionId, data.payload)
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        async fetchCouponsHistory({ commit }) {
            let Service = new CouponsHistoryService();
            let history = await Service.getCouponsHistoryService();
            commit('updateCouponsHistory', history);
        },

        resetAccount({ commit }) {
            commit('resetState');
        },

        resetSignUpCouponCanvasser({ commit }) {
            commit('resetCouponState');
        },

        async fetchStripeSetupIntent({ commit }, payload) {
            let service = new UserService();
            commit('setFetchingSetupIntent', true);
            let setupIntent = await service.fetchStripeSetupIntent(payload);
            commit('setFetchingSetupIntent', false);
            commit('setSetupIntent', setupIntent.data.intent_client_secret);
        },

        fetchPromotionalBanners({ commit, state, rootGetters, dispatch }, forceReload = false) {
            let service = new UserService();
            return new Promise((resolve, reject) => {
                try {
                    service.fetchPromotionalBanners()
                        .then(response => {
                            commit('setPromotionalBanners', response.data.data);
                            resolve(response);
                        })
                        .catch(error => {
                            console.error({ error });
                            reject(error);
                        });
                } catch (err) {
                    Sentry.captureException(err);
                }
            });
        },

        fetchThemes({ commit }) {
            let service = new UserService();
            return new Promise((resolve, reject) => {
                try {
                    service.fetchThemes()
                        .then(response => {
                            commit('setTheme', response.data.data);
                            resolve(response);
                        })
                        .catch(error => {
                            console.error({ error });
                            reject(error);
                        });
                } catch (err) {
                    Sentry.captureException(err);
                }
            });
        },

    },
    getters: {
        getExpandFormName: (state) => state.expandFormName,

        getFirstName: (state, getters) => state.user.hasFetched ? getters.getUser.first_name : null,

        getLastName: (state, getters) => state.user.hasFetched ? getters.getUser.last_name : null,

        getEmail: (state, getters) => state.user.hasFetched ? getters.getUser.email : null,

        getFullName: (state, getters) => {
            return state.user.hasFetched ? capitalizeFirstLetter(`${getters.getFirstName} ${getters.getLastName}`) : null;
        },

        getFlowerBondStatus: state => state.user.hasFetched && state.user.data?.bond_holder,

        getUser: state => state.user.hasFetched && state.user.data ? state.user.data : null,

        getUserBanners: state => state.user.hasFetched && state.user.banners.data ? state.user.banners.data : null,

        getUserId: state => state.user.hasFetched && state.user.data ? state.user.data.id : null,

        getAllowPaused: state => state.user.hasFetched ? state.user.data?.allow_paused : false,

        getUserFriendCode: state => state.user.hasFetched ? state.user.data?.friend_code : false,

        getUserReferralsRemaining: state => state.user.hasFetched ? state.user.data?.referrals_remaining : null,

        getUserInvitesRemaining: state => state.user.hasFetched ? state.user.data?.invites_remaining : null,

        getRescheduledDeliveryDays: state => state.rescheduledDeliveryDays.data,

        getHasAnsweredCountrySwitch: state => state.countrySwitch.hasAnswered,

        getCoupons: state => state.user.hasFetched && state.user.data?.coupons,

        getCouponsHistory: state => state.CouponsHistory ? state.CouponsHistory : [],

        getSignupCoupon: state => state.signupCoupon,

        getLapserOptionsEnabled: state => state.user.data?.change_plan_on_restart,

        getLilyFamilyOptOutEnabled: state => state.user.data?.lily_family_opt_out,

        getFourWeeklyRestart: state => state.user.data?.four_weekly_restart,

        getOutstandingOrders: state => state.outstandingOrders,

        getHasEssentialBox: state => state.user.data?.has_essential_box,

        getSkipReaasons: state => state.skipReasons,

        getSkipDeliveryPopupEnabled: state => state.user.data?.skipping_delivery_popup,

        getHolidayGiftingEnabled: state => state.holidayGiftingEnabled,

        getSkipConsecutiveHolidayEnabled: state => state.skipConsecutiveHolidayEnabled,

        getShiftDeliveryScheduleEnabled(state, getters, rootState, rootGetters) {
            const subscription = rootGetters['subscription/getActiveSubscription'];
            return state.shiftDeliveryScheduleEnabled && subscription && subscription.plan_interval !== 1;
        },

        getScrollbarEnabled: state => state.scrollbarEnabled,

        getShopInGiftingEnabled: state => state.user.data?.shop_enabled_in_gifting,

        getGiftingVaseEnabled: state => process.env.VUE_APP_MIX_GIFTING_ENABLE_VASE === 'true' && !state.user.data?.shop_enabled_in_gifting,

        getSpecialDatesCoupon: state => state.user?.data?.special_dates,

        getSpecialDatesEnabled: state => process.env.VUE_APP_SPECIAL_DATES_ENABLE === 'true' || state.user?.data?.special_dates_enabled,

        getBumperBoxEnabled: state => state.user.data?.change_primary_product_from_dashboard,

        getPrimaryProductInGiftingEnabled: state => state.user.data?.choose_primary_product_in_gifting,

        getSetupIntent: state => state.setupIntent,

        isfetchingSetupIntent: state => state.fetchingSetupIntent,

        hasError: state => state.hasError,

        getEssentialBoxRestart: state => state.user.data?.essential_box_restart,

        isMonthlySubscriptionEnabled: state => state.user.data?.four_weekly_restart,

        showFloatingBanner: state => state.showFloatingBanner,

        getBannerMessage: state => state.bannerMessage,

        getCanPauseOrCancel: state => state.canPauseOrCancel,

        getIsPauseFlow: state => state.isPauseFlow,

        getFieldsData: state => state.updateYourPlanData,

        getrestartFields: state => state.restartFields,

        getAllowCancel: state => state.user.hasFetched ? state.user.data?.allow_cancel : false,

        getUserCanRefer: state => state.user.hasFetched && state.user.data?.can_refer,

        getUserHasPaidOrders: state => state.user.hasFetched && state.user.data?.having_paid_orders,

        getUserHasFailedLastPayment: state => state.user.hasFetched && state.user.data?.is_last_order_outstanding,

        isThreeWeeklyEnabled: state => state.user.hasFetched ? state.user.data?.three_weekly_restart : false,

        getPauseFeedbackOptions: state => state.pauseFeedbackOptions,

        getPopSingleGift: state => state.user.data.pop_single_gifting,

        getPopMultiGift: state => state.user.data.pop_multi_gifting,

        getDashboardV2Enabled: state => state.user.data?.new_dashboard,

        getPromotionalBanners: state => state.promotionalBanners,

        isMobile: state => state.isMobile,

        isDesktop: state => state.isDesktop,

        isFreeVase: state => state.user?.data?.free_vase_gift_oneoff,

        getNextPossibleDate: state => state.user?.data?.next_possible_delivery_date,

        getThemes: state => state.themes,
    },
};
